// BattleSheet — fullscreen kid PvE battle UI. Pet on the bottom,
// monster up top, HP bars on each, card-deck strip below so each
// tap = pick a card to attack with.
//
// Critical rule (enforced server-side): every attack spends a card
// via spend_kid_card. No cards left = no attacks. Parent-gated
// economy stays intact — battles consume the same resource the
// feeding loop produces.
//
// EMOJI-IN-UI EXCEPTION: kids-mode opt-in.

import { useEffect, useState } from "react";
import { COLOR, FONT, RADIUS } from "../../lib/tokens";
import { supabase } from "../../lib/supabase";
import { useKidBattle } from "../../lib/useKidBattle";
import { useKidPetState } from "../../lib/useKidPetState";
import { playKidSfx } from "../../lib/kidSfx";
import { KID_CARD_TYPES } from "./cardTypes";
import { statsFromPet, decayLabel } from "./petStats";

const CARD_ROLE = {
  build:  { role: "HEAVY",  hint: "Big hit"      },
  spark:  { role: "CRIT",   hint: "Chance for 2x"},
  super:  { role: "HEAL",   hint: "Restore HP"   },
  boost:  { role: "DOUBLE", hint: "Two quick hits"},
  water:  { role: "MANA",   hint: "Refills mana" },
  dairy:  { role: "BLOCK",  hint: "Skips counter"},
};

export function BattleSheet({ userId, onClose }) {
  const { pet, activeStats } = useKidPetState(userId);
  const { active, busy, error, start, attack, flee } = useKidBattle(userId);
  const stats = statsFromPet(pet, activeStats);
  const fadeLabel = decayLabel(activeStats?.earliestAt);
  const totalActiveBuff = (stats.atk + stats.def + stats.agi + stats.satk + stats.sdef);
  const [monsters, setMonsters] = useState([]);
  const [loadingMonsters, setLoadingMonsters] = useState(true);
  const [endShown, setEndShown] = useState(null); // "won" | "lost" | "fled"

  useEffect(() => {
    let alive = true;
    (async () => {
      const { data } = await supabase
        .from("monsters")
        .select("slug, name, emoji, hp, attack_min, attack_max, level_required, sort_order, blurb")
        .order("sort_order", { ascending: true });
      if (!alive) return;
      setMonsters(data || []);
      setLoadingMonsters(false);
    })();
    return () => { alive = false; };
  }, []);

  // Surface win/lose overlay when the battle terminates (the active
  // hook clears `active` for fled; for won/lost the row's status
  // changes and `active` query filter no longer matches).
  useEffect(() => {
    if (active?.status === "won")  { setEndShown("won");  playKidSfx("fanfare"); }
    if (active?.status === "lost") { setEndShown("lost"); playKidSfx("thunk");   }
  }, [active?.status]);

  const counts = {
    super: pet?.cards_super ?? 0,
    build: pet?.cards_build ?? 0,
    boost: pet?.cards_boost ?? 0,
    spark: pet?.cards_spark ?? 0,
    water: pet?.cards_water ?? 0,
    dairy: pet?.cards_dairy ?? 0,
  };
  const totalCards = Object.values(counts).reduce((a, b) => a + b, 0);

  const battling = !!active && active.status === "active";
  const currentMonster = battling
    ? monsters.find((m) => m.slug === active.monster_slug)
    : null;

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "#0E1620",
        zIndex: 350, display: "flex", flexDirection: "column",
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
        color: "#fff",
      }}
    >
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 16px",
      }}>
        <button type="button" onClick={onClose}
          style={{ background: "transparent", border: "none", color: "#fff", fontSize: 28, padding: 4, cursor: "pointer" }}
          aria-label="Close">✕</button>
        <div style={{ fontFamily: FONT.itemTitle, fontStyle: "italic", fontSize: 20 }}>
          {battling ? "⚔️ Battle" : "Pick a Monster"}
        </div>
        <div style={{ width: 32 }} />
      </div>

      {/* PRE-BATTLE: monster picker */}
      {!battling && !endShown && (
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 16px 24px" }}>
          {loadingMonsters ? (
            <div style={{ padding: 24, textAlign: "center", color: "#88a" }}>Loading monsters…</div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {monsters.map((m) => {
                const locked = (pet?.level || 1) < m.level_required;
                return (
                  <button
                    key={m.slug}
                    type="button"
                    onClick={async () => {
                      if (locked || busy) return;
                      playKidSfx("thunk");
                      const r = await start(m.slug);
                      if (r) setEndShown(null);
                    }}
                    disabled={locked || busy}
                    className="mcm-focusable"
                    style={{
                      all: "unset", cursor: locked ? "not-allowed" : "pointer",
                      background: locked ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.10)",
                      border: `1.5px solid ${locked ? "rgba(255,255,255,0.10)" : "rgba(255,255,255,0.25)"}`,
                      borderRadius: 14, padding: 14,
                      display: "flex", flexDirection: "column", alignItems: "center",
                      gap: 6, textAlign: "center",
                      opacity: locked ? 0.45 : 1,
                      touchAction: "manipulation",
                    }}
                  >
                    <div style={{ fontSize: 44, lineHeight: 1 }}>{m.emoji}</div>
                    <div style={{ fontFamily: FONT.itemTitle, fontStyle: "italic", fontSize: 16 }}>{m.name}</div>
                    <div style={{ fontFamily: FONT.mono, fontSize: 10, letterSpacing: "0.10em", color: "#aab" }}>
                      LV {m.level_required} · HP {m.hp}
                    </div>
                    {m.blurb && (
                      <div style={{ fontFamily: FONT.forma, fontSize: 11, color: "#ccd", marginTop: 4 }}>{m.blurb}</div>
                    )}
                    {locked && (
                      <div style={{ fontFamily: FONT.mono, fontSize: 10, color: "#FFB347", marginTop: 4 }}>
                        🔒 needs LV {m.level_required}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* IN-BATTLE: monster bar + pet bar + card deck */}
      {battling && currentMonster && (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "8px 16px 16px" }}>
          {/* Monster card */}
          <div style={{
            background: "linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.04))",
            border: "1.5px solid rgba(255,255,255,0.20)",
            borderRadius: 18, padding: 16, textAlign: "center", marginBottom: 16,
          }}>
            <div style={{ fontSize: 64, lineHeight: 1 }}>{currentMonster.emoji}</div>
            <div style={{ fontFamily: FONT.itemTitle, fontStyle: "italic", fontSize: 20, marginTop: 6 }}>{currentMonster.name}</div>
            <HpBar
              hp={active.monster_hp_remaining}
              max={active.monster_hp_start}
              color="#E84A5F"
            />
          </div>

          {/* Pet bar */}
          <div style={{
            background: "rgba(123,156,92,0.18)",
            border: "1.5px solid rgba(123,156,92,0.45)",
            borderRadius: 14, padding: 12, textAlign: "center", marginBottom: 16,
          }}>
            <div style={{ fontFamily: FONT.itemTitle, fontStyle: "italic", fontSize: 16 }}>
              {pet?.pet_name || "Your Pet"} · LV {pet?.level ?? 1}
            </div>
            <HpBar
              hp={active.pet_hp_remaining}
              max={active.pet_hp_start}
              color="#7B9C5C"
            />
            {totalActiveBuff > 0 && (
              <div style={{
                display: "flex", justifyContent: "center", flexWrap: "wrap",
                gap: 6, marginTop: 8,
              }}>
                {stats.atk > 0 && <BuffChip emoji="💪" label="ATK" value={stats.atk} tint="#D96B2B" />}
                {stats.def > 0 && <BuffChip emoji="🛡️" label="DEF" value={stats.def} tint="#C8A86A" />}
                {stats.agi > 0 && <BuffChip emoji="⚡" label="AGI" value={stats.agi} tint="#D4A637" />}
                {stats.satk > 0 && <BuffChip emoji="🥦" label="S.ATK" value={stats.satk} tint="#2F8F83" />}
                {stats.sdef > 0 && <BuffChip emoji="✨" label="S.DEF" value={stats.sdef} tint="#7A4E2D" />}
                {fadeLabel && (
                  <span style={{
                    fontFamily: FONT.mono, fontSize: 9, color: "#FFB347",
                    letterSpacing: "0.08em", alignSelf: "center", marginLeft: 4,
                  }}>
                    {fadeLabel}
                  </span>
                )}
              </div>
            )}
            {totalActiveBuff === 0 && (
              <div style={{
                marginTop: 6, fontFamily: FONT.mono, fontSize: 10,
                color: "#aab", letterSpacing: "0.08em",
              }}>
                NO ACTIVE BUFFS — eat real food to power up
              </div>
            )}
          </div>

          {/* Card deck */}
          <div style={{ marginTop: "auto" }}>
            {totalCards === 0 && (
              <div style={{
                background: "rgba(255,180,71,0.15)",
                border: "1px solid rgba(255,180,71,0.4)",
                borderRadius: 10, padding: 12, textAlign: "center",
                fontFamily: FONT.forma, fontSize: 13, color: "#FFD494",
                marginBottom: 8,
              }}>
                Out of cards! Eat some food at home to get more.
              </div>
            )}
            <div style={{
              display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8,
            }}>
              {KID_CARD_TYPES.map((c) => {
                const count = counts[c.key] || 0;
                const role = CARD_ROLE[c.key];
                const disabled = count <= 0 || busy;
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => { if (!disabled) attack(c.key); }}
                    disabled={disabled}
                    className="mcm-focusable"
                    style={{
                      all: "unset", cursor: disabled ? "not-allowed" : "pointer",
                      background: disabled ? "rgba(255,255,255,0.04)" : `${c.tint}33`,
                      border: `1.5px solid ${disabled ? "rgba(255,255,255,0.10)" : c.tint}`,
                      borderRadius: 12, padding: 8,
                      display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
                      opacity: disabled ? 0.45 : 1,
                      touchAction: "manipulation",
                    }}
                  >
                    <div style={{ fontSize: 22, lineHeight: 1 }}>{c.emoji}</div>
                    <div style={{ fontFamily: FONT.mono, fontSize: 10, fontWeight: 800, letterSpacing: "0.06em" }}>
                      {role?.role || c.label.toUpperCase()}
                    </div>
                    <div style={{ fontFamily: FONT.mono, fontSize: 11, fontWeight: 700 }}>
                      ×{count}
                    </div>
                  </button>
                );
              })}
            </div>
            <div style={{ display: "flex", justifyContent: "center", marginTop: 10 }}>
              <button type="button" onClick={flee} disabled={busy}
                style={{
                  background: "transparent", border: "1px solid rgba(255,255,255,0.25)",
                  color: "#aab", borderRadius: 999, padding: "8px 18px",
                  fontFamily: FONT.mono, fontSize: 11, letterSpacing: "0.10em",
                  cursor: "pointer", touchAction: "manipulation",
                }}>
                FLEE
              </button>
            </div>
            {error && (
              <div style={{ marginTop: 8, fontFamily: FONT.forma, fontSize: 12, color: "#FF8A8A", textAlign: "center" }}>
                {error}
              </div>
            )}
          </div>
        </div>
      )}

      {/* END overlay */}
      {endShown && (
        <div
          onClick={() => { setEndShown(null); onClose?.(); }}
          style={{
            position: "absolute", inset: 0, background: "rgba(0,0,0,0.7)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 10,
          }}
        >
          <div style={{
            background: "#1a2533", border: "2px solid #fff", borderRadius: RADIUS.xxl,
            padding: 32, textAlign: "center", maxWidth: 320,
          }}>
            <div style={{ fontSize: 64 }}>{endShown === "won" ? "🏆" : "💤"}</div>
            <div style={{ fontFamily: FONT.itemTitle, fontStyle: "italic", fontSize: 28, marginTop: 8 }}>
              {endShown === "won" ? "Victory!" : "Maybe Next Time"}
            </div>
            <div style={{ fontFamily: FONT.forma, fontSize: 14, color: "#aab", marginTop: 8 }}>
              {endShown === "won"
                ? "Your pet got stronger from the food you ate today!"
                : "Eat some real food to power up your pet, then try again!"}
            </div>
            <div style={{
              fontFamily: FONT.mono, fontSize: 11, color: COLOR.muted, marginTop: 12, letterSpacing: "0.10em",
            }}>
              TAP TO CLOSE
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BuffChip({ emoji, label, value, tint }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "3px 8px",
      background: `${tint}33`,
      border: `1px solid ${tint}88`,
      borderRadius: 999,
      fontFamily: FONT.mono, fontSize: 11, fontWeight: 700,
      color: "#fff",
    }}>
      <span style={{ fontSize: 12 }}>{emoji}</span>
      <span style={{ letterSpacing: "0.06em" }}>{label}</span>
      <span style={{ fontVariantNumeric: "tabular-nums" }}>+{value}</span>
    </span>
  );
}

function HpBar({ hp, max, color }) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (hp / max) * 100)) : 0;
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{
        height: 14, borderRadius: 7,
        background: "rgba(255,255,255,0.10)",
        overflow: "hidden", position: "relative",
      }}>
        <div style={{
          width: `${pct}%`, height: "100%", background: color,
          transition: "width 240ms ease",
        }} />
      </div>
      <div style={{ fontFamily: FONT.mono, fontSize: 11, marginTop: 4, color: "#ccd" }}>
        {hp} / {max}
      </div>
    </div>
  );
}

export default BattleSheet;
